import type { RefObject } from 'react';
import type { AppFramework } from '@hangar/shared';
import { CONNECTABLE_FRAMEWORKS } from './easSetupModel';
import { isNativeFramework } from './appFormModel';
import { SLUG_HINT } from '@/lib/format';
import { FormField, fieldA11y } from '@/components/FormField';
import { FRAMEWORK_LABEL } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import { useSeesProviders } from '@/lib/useAudience';
import { BitriseAppField } from './BitriseAppField';
import type { useConnectAppForm } from './useConnectAppForm';

/** The state `useConnectAppForm` returns; every field reads its value, message and handler from it. */
export type ConnectState = ReturnType<typeof useConnectAppForm>;

const MONO = 'font-mono sm:text-[13px]';

export function NameField({ f }: { f: ConnectState }) {
  return (
    <FormField id="app-name" label="Name" required error={f.errors.name}>
      <Input data-testid="app-name" value={f.values.name} placeholder="My app" autoComplete="off" maxLength={120} onChange={(e) => f.change('name', e.target.value)} {...fieldA11y('app-name', f.errors.name)} />
    </FormField>
  );
}

export function SlugField({ f, slugRef }: { f: ConnectState; slugRef?: RefObject<HTMLInputElement | null> }) {
  return (
    <FormField id="app-slug" label="Slug" required hint={SLUG_HINT} error={f.errors.slug}>
      <Input
        ref={slugRef}
        data-testid="app-slug"
        className={MONO}
        value={f.values.slug}
        placeholder="my-app"
        autoComplete="off"
        spellCheck={false}
        maxLength={63}
        onChange={(e) => f.change('slug', e.target.value)}
        {...fieldA11y('app-slug', f.errors.slug, SLUG_HINT)}
      />
    </FormField>
  );
}

/** One framework option; the native ones wait for the API to report `bitrise`. */
function frameworkOption(framework: AppFramework, nativeBuilds: boolean) {
  const waiting = isNativeFramework(framework) && !nativeBuilds;
  return { value: framework, label: waiting ? `${FRAMEWORK_LABEL[framework]} (not available on this Hangar)` : FRAMEWORK_LABEL[framework], disabled: waiting };
}

export function FrameworkField({ f }: { f: ConnectState }) {
  const hint = f.nativeBuilds ? undefined : 'This Hangar builds Expo apps; native builds are not enabled here';
  return (
    <FormField id="app-framework" label="Framework" required hint={hint} error={f.errors.framework}>
      <OptionSelect
        data-testid="app-framework"
        value={f.values.framework}
        onValueChange={(framework) => f.change('framework', framework)}
        placeholder="Select a framework"
        options={CONNECTABLE_FRAMEWORKS.map((framework) => frameworkOption(framework, f.nativeBuilds))}
        {...fieldA11y('app-framework', f.errors.framework, f.nativeBuilds ? undefined : 'framework-hint')}
      />
    </FormField>
  );
}

export function WorkingDirectoryField({ f }: { f: ConnectState }) {
  return (
    <FormField id="app-workingDirectory" label="Working directory" required hint="Relative to the repository root · . for the root, apps/mobile in a monorepo" error={f.errors.workingDirectory}>
      <Input data-testid="app-working-directory" className={MONO} value={f.values.workingDirectory} spellCheck={false} maxLength={200} onChange={(e) => f.change('workingDirectory', e.target.value)} {...fieldA11y('app-workingDirectory', f.errors.workingDirectory, 'wd-hint')} />
    </FormField>
  );
}

function EasProjectIdField({ f }: { f: ConnectState }) {
  return (
    <div className="flex flex-col gap-1 sm:col-span-2">
      <FormField id="app-easProjectId" label="EAS project id" hint="Advanced · only for a project that already exists on Expo. Attributes EAS webhooks to this app." error={f.errors.easProjectId}>
        <Input
          data-testid="app-eas-project-id"
          className={MONO}
          value={f.values.easProjectId}
          placeholder="8f2e6c1a-9b3d-4e5f-a6b7-c8d9e0f1a2b3"
          spellCheck={false}
          maxLength={100}
          onChange={(e) => f.change('easProjectId', e.target.value)}
          {...fieldA11y('app-easProjectId', f.errors.easProjectId, 'eas-hint')}
        />
      </FormField>
      <Button type="button" size="sm" variant="ghost" className="w-fit" data-testid="app-eas-create-instead" onClick={() => f.setLinkEasExisting(false)}>
        Let Hangar create it instead
      </Button>
    </div>
  );
}

/** Hangar creates the app's EAS project in the client's Expo organization; the id field is an advanced override behind a button. */
function CreateOnEasNote({ f }: { f: ConnectState }) {
  return (
    <div data-testid="app-eas-create-note" className="flex flex-col gap-1.5 rounded-lg border border-dashed px-3 py-2.5 text-[13px] sm:col-span-2">
      <p className="font-medium text-foreground">Hangar creates the EAS project for you</p>
      <p className="text-muted-foreground">Right after connecting, Hangar creates this app&apos;s project in your organization&apos;s Expo account and fills in its id. No expo.dev steps.</p>
      <Button type="button" size="sm" variant="ghost" className="w-fit" data-testid="app-eas-link-existing" onClick={() => f.setLinkEasExisting(true)}>
        Link an existing EAS project instead
      </Button>
    </div>
  );
}

/** Source control creates the Bitrise app for the user; this note says so and offers to link an existing one instead. */
function CreateOnBitriseNote({ f }: { f: ConnectState }) {
  return (
    <div data-testid="app-bitrise-create-note" className="flex flex-col gap-1.5 rounded-lg border border-dashed px-3 py-2.5 text-[13px]">
      <p className="font-medium text-foreground">Hangar creates the Bitrise app for you</p>
      <p className="text-muted-foreground">After connecting, pick the Bitbucket repository: Hangar registers the app on Bitrise and stores its workflows; builds get their source from Hangar. No bitrise.io steps, no SSH key.</p>
      <Button type="button" size="sm" variant="ghost" className="w-fit" data-testid="app-bitrise-link-existing" onClick={() => f.setLinkExisting(true)}>
        Link an existing Bitrise app instead
      </Button>
    </div>
  );
}

/** The way back to "Create on Bitrise" once the person chose to link an existing app. */
function CreateInsteadButton({ f }: { f: ConnectState }) {
  if (!f.sourceControl || !f.linkExisting) return null;
  return (
    <Button type="button" size="sm" variant="ghost" className="mt-1 w-fit" onClick={() => f.setLinkExisting(false)}>
      Create it on Bitrise instead
    </Button>
  );
}

function BitriseLinkField({ f }: { f: ConnectState }) {
  return (
    <>
      <BitriseAppField idPrefix="app" value={f.values.bitriseAppSlug} onChange={f.setBitriseAppSlug} error={f.bitriseError} />
      <CreateInsteadButton f={f} />
    </>
  );
}

/** What a client reads instead of the provider fields (SPEC decision 23): Hangar or Malible prepares the builds. */
function ClientSetupNote({ native }: { native: boolean }) {
  return (
    <div data-testid="app-setup-note" className="flex flex-col gap-1.5 rounded-lg border border-dashed px-3 py-2.5 text-[13px] sm:col-span-2">
      <p className="font-medium text-foreground">{native ? 'Hangar prepares native builds' : 'Hangar sets up the builds for you'}</p>
      <p className="text-muted-foreground">{native ? 'Hangar prepares native builds for this app after you connect it: nothing to set up outside Hangar.' : 'Right after connecting, Hangar prepares how this app is built. Nothing to set up outside Hangar.'}</p>
    </div>
  );
}

/**
 * Where the app builds: a Bitrise app for native apps, an (optional) EAS project for the others (platform operators);
 * clients read a neutral note and send the same defaults (Hangar creates the project, Malible the native setup).
 */
export function BuildProviderField({ f }: { f: ConnectState }) {
  if (!useSeesProviders()) return <ClientSetupNote native={f.onBitrise} />;
  if (!f.onBitrise) return f.linkEasExisting ? <EasProjectIdField f={f} /> : <CreateOnEasNote f={f} />;
  return <div className="sm:col-span-2">{f.createOnBitrise ? <CreateOnBitriseNote f={f} /> : <BitriseLinkField f={f} />}</div>;
}
